import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [consentText, setConsentText] = useState(false);
  const [consentOptOut, setConsentOptOut] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setSubmitMessage('Please fill in all fields');
      setTimeout(() => setSubmitMessage(''), 3000);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitMessage('Message must be 500 characters or less');
      setTimeout(() => setSubmitMessage(''), 3000);
      return;
    }

    if (!consentText || !consentOptOut) {
      setSubmitMessage('Please review and accept all consent checkboxes before submitting.');
      setTimeout(() => setSubmitMessage(''), 4000);
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const form = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('consent_text', consentText ? 'Yes' : 'No');
      formDataToSend.append('consent_optout', consentOptOut ? 'Yes' : 'No');

      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitMessage('Thank you! We will get back to you soon.');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setConsentText(false);
        setConsentOptOut(false);
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Send Us a Message</h2>
          <p className="text-lg text-gray-600">
            Fill out the form below and we'll respond as soon as possible
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <form
            id="contact_form"
            data-readdy-form
            action="https://readdy.ai/api/form/d6s21d5sbgrc47fimqg0"
            method="POST"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-orange-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C4F8A] focus:border-transparent text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-orange-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C4F8A] focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number <span className="text-orange-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C4F8A] focus:border-transparent text-sm"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Message <span className="text-orange-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C4F8A] focus:border-transparent text-sm resize-none"
                placeholder="Tell us how we can help you..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-2">
                {formData.message.length}/500 characters
              </p>
            </div>

            {/* Consent Checkboxes */}
            <div className="border border-gray-200 rounded-lg p-5 space-y-4 bg-gray-50">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Consent &amp; Communication Preferences</p>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="consent_text"
                  checked={consentText}
                  onChange={(e) => setConsentText(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-[#5C4F8A] cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">I agree to receive text messages</strong> from this company regarding my inquiry, appointment reminders, and service updates at the phone number provided above. Message and data rates may apply. Message frequency may vary.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="consent_optout"
                  checked={consentOptOut}
                  onChange={(e) => setConsentOptOut(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-[#5C4F8A] cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-gray-900">I understand I can opt out at any time</strong> by replying <strong>STOP</strong> to any text message I receive. I acknowledge that opting out will stop all text communications from this company.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#5C4F8A] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#4A3D72] transition-all duration-300 text-base whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-spinner fa-spin"></i>
                  Sending...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-paper-plane"></i>
                  Send Message
                </span>
              )}
            </button>

            {submitMessage && (
              <div className={`text-center p-4 rounded-lg ${
                submitMessage.includes('Thank you')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}>
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}